import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/AppLayout';
import { S, Icon, PageHeader, DataCard, BtnPrimary, BtnSecondary, TH, TD } from '../regureStyles';
import AMS360Drawer from './AMS360Drawer';

// ─── Connector Data ──────────────────────────────────────
const INITIAL_CONNECTORS = [
  { id: 'ams360', name: 'AMS360 (Vertafore)', category: 'Agency Management', status: 'not_configured', description: 'Bi-directional sync of prospects, clients, policies, and documents with Vertafore AMS360', letter: 'A', configurable: true },
  { id: 'epic', name: 'Applied Epic', category: 'Agency Management', status: 'coming_soon', description: 'Integration with Applied Systems Epic for agency operations', letter: 'E', configurable: false },
  { id: 'hawksoft', name: 'HawkSoft', category: 'Agency Management', status: 'coming_soon', description: 'Integration with HawkSoft CMS for independent agencies', letter: 'H', configurable: false },
  { id: 'winbiz', name: 'Winbiz', category: 'Accounting', status: 'not_configured', description: 'Swiss accounting system integration for commission and invoice reconciliation', letter: 'W', configurable: true },
  { id: 'quickbooks', name: 'QuickBooks Online', category: 'Accounting', status: 'not_configured', description: 'Accounting sync for US small and mid-sized operations', letter: 'Q', configurable: true },
  { id: 'xero', name: 'Xero', category: 'Accounting', status: 'coming_soon', description: 'Cloud accounting integration for international operations', letter: 'X', configurable: false },
  { id: 'swiss_claims', name: 'Swiss Claims Network', category: 'Claims Administration', status: 'not_configured', description: 'Delegated claims management integration for Swiss MGAs', letter: 'S', configurable: true },
  { id: 'lloyds', name: "Lloyd's Coverholder Reporting", category: 'Carrier', status: 'coming_soon', description: "Bordereaux and performance reporting to Lloyd's syndicates", letter: 'L', configurable: false },
  { id: 'stamping', name: 'Surplus Lines Stamping Offices', category: 'Compliance', status: 'not_configured', description: 'Automated filing to state stamping offices (CA, TX, FL, etc.)', letter: 'S', configurable: true },
  { id: 'docusign', name: 'DocuSign', category: 'Document Signing', status: 'connected', description: 'E-signature workflow integration (native alternative: Regure E-Sign)', letter: 'D', configurable: true },
];

const STATUS_CFG = {
  connected:      { bg: '#dcfce7', color: '#15803d', label: 'Connected' },
  not_configured: { bg: '#fef3c7', color: '#92400e', label: 'Not Configured' },
  coming_soon:    { bg: '#f1f5f9', color: '#64748b', label: 'Coming Soon' },
  active:         { bg: '#dcfce7', color: '#15803d', label: 'Active' },
  error:          { bg: '#fee2e2', color: '#991b1b', label: 'Error' },
  paused:         { bg: '#fef3c7', color: '#92400e', label: 'Paused' },
};

const CATEGORY_COLORS = {
  'Agency Management': '#4338ca',
  'Accounting': '#0369a1',
  'Claims Administration': '#9d174d',
  'Carrier': '#92400e',
  'Compliance': '#065f46',
  'Document Signing': '#7c3aed',
};

// ═══════════════════════════════════════════════════════════
const IntegrationsScreen = () => {
  const { t } = useTranslation();
  const [connectors, setConnectors] = useState(INITIAL_CONNECTORS);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerConnector, setDrawerConnector] = useState(null);
  const [toast, setToast] = useState('');

  const openDrawer = (connector) => {
    setDrawerConnector(connector);
    setDrawerOpen(true);
  };

  const handleSave = (connectorId) => {
    setConnectors(prev => prev.map(c => c.id === connectorId ? { ...c, status: 'connected' } : c));
    setDrawerOpen(false);
    setToast(t('screens.integrations.configSaved', { name: connectors.find(c => c.id === connectorId)?.name || '' }));
    setTimeout(() => setToast(''), 3000);
  };

  const activeIntegrations = connectors.filter(c => c.status === 'connected');

  return (
    <AppLayout>
      <PageHeader
        breadcrumb={<><span>{t('screens.integrations.breadcrumb')}</span> <Icon name="chevronRight" size={10} /> <span>{t('screens.integrations.title')}</span></>}
        title={t('screens.integrations.title')}
        actions={
          <BtnSecondary onClick={() => window.location.hash = '#/audit-logs'} style={{ gap: 6 }}>
            <Icon name="lock" color={S.textSecondary} size={14} />
            {t('screens.integrations.viewAudit')}
          </BtnSecondary>
        }
      />

      <div style={{ flex: 1, overflow: 'auto', padding: '24px 32px' }}>

        {/* ── Integration Activity Stats ──────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
          <StatCard label={t('screens.integrations.apiCallsToday')} value="247" icon="activity" color={S.burgundy} />
          <StatCard label={t('screens.integrations.successfulSyncs')} value="245" icon="check-circle" color={S.success} />
          <StatCard label={t('screens.integrations.failedSyncs')} value="2" icon="alert-circle" color={S.danger} />
          <StatCard label={t('screens.integrations.avgResponseTime')} value="234ms" icon="clock" color={S.info} />
        </div>

        {/* ── Active Integrations Table ───────────────── */}
        {activeIntegrations.length > 0 && (
          <DataCard style={{ padding: 0, overflow: 'hidden', marginBottom: 28 }}>
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${S.borderLight}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary }}>{t('screens.integrations.activeIntegrations')}</div>
              <div style={{ fontSize: 12, color: S.textMuted }}>{t('screens.integrations.connected', { count: activeIntegrations.length })}</div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: S.bgCard }}>
                  <TH>{t('screens.integrations.colName')}</TH><TH>{t('screens.integrations.colCategory')}</TH><TH>{t('screens.integrations.colStatus')}</TH><TH>{t('screens.integrations.colLastSync')}</TH><TH>{t('screens.integrations.colRecordsSynced')}</TH><TH>{t('screens.integrations.colActions')}</TH>
                </tr></thead>
                <tbody>
                  {activeIntegrations.map(c => (
                    <tr key={c.id} style={{ borderBottom: `1px solid ${S.borderLight}` }}
                      onMouseEnter={e => { e.currentTarget.style.background = S.bgHover; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                      <TD style={{ fontWeight: 600, color: S.textPrimary }}>{c.name}</TD>
                      <TD><CategoryBadge category={c.category} /></TD>
                      <TD><StatusBadge status="active" /></TD>
                      <TD>{t('screens.integrations.hoursAgo')}</TD>
                      <TD>47</TD>
                      <TD>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={() => {}} style={linkBtnStyle}>{t('screens.integrations.viewLogs')}</button>
                          <button onClick={() => openDrawer(c)} style={linkBtnStyle}>{t('screens.integrations.configure')}</button>
                        </div>
                      </TD>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DataCard>
        )}

        {/* ── Available Connectors Grid ───────────────── */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary, marginBottom: 16 }}>{t('screens.integrations.availableConnectors')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
            {connectors.map(c => (
              <ConnectorCard key={c.id} connector={c} onConfigure={() => openDrawer(c)} />
            ))}
          </div>
        </div>
      </div>

      {/* ── AMS360 Drawer ────────────────────────────── */}
      {drawerOpen && drawerConnector && (
        <AMS360Drawer
          connector={drawerConnector}
          onClose={() => setDrawerOpen(false)}
          onSave={() => handleSave(drawerConnector.id)}
        />
      )}

      {/* ── Toast ────────────────────────────────────── */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 32, right: 32, zIndex: 10000,
          background: '#065f46', color: '#fff', padding: '12px 20px',
          borderRadius: 10, fontSize: 13, fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 8,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          animation: 'toastIn 0.3s ease',
        }}>
          <Icon name="check-circle" color="#fff" size={16} />
          {toast}
        </div>
      )}
      <style>{`@keyframes toastIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </AppLayout>
  );
};

// ─── Sub-components ──────────────────────────────────────
const linkBtnStyle = {
  background: 'none', border: 'none', color: S.burgundy,
  fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '2px 0',
  textDecoration: 'underline', textDecorationColor: 'transparent',
};

const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const STATUS_KEY_MAP = {
    connected: 'screens.integrations.statusConnected',
    not_configured: 'screens.integrations.statusNotConfigured',
    coming_soon: 'screens.integrations.statusComingSoon',
    active: 'screens.integrations.statusActive',
    error: 'screens.integrations.statusError',
    paused: 'screens.integrations.statusPaused',
  };
  const cfg = STATUS_CFG[status] || STATUS_CFG.not_configured;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 12,
      fontSize: 11, fontWeight: 600, background: cfg.bg, color: cfg.color,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.color }} />
      {t(STATUS_KEY_MAP[status] || 'screens.integrations.statusNotConfigured')}
    </span>
  );
};

const CategoryBadge = ({ category }) => {
  const color = CATEGORY_COLORS[category] || '#64748b';
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600,
      background: color + '14', color,
    }}>
      {category}
    </span>
  );
};

const ConnectorCard = ({ connector, onConfigure }) => {
  const { t } = useTranslation();
  const c = connector;
  const isSoon = c.status === 'coming_soon';
  return (
    <div style={{
      background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 14,
      padding: 20, display: 'flex', flexDirection: 'column', gap: 12,
      opacity: isSoon ? 0.7 : 1,
      transition: 'box-shadow 0.15s',
    }}
      onMouseEnter={e => { if (!isSoon) e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2 || '#722f37'})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 16, fontWeight: 800, flexShrink: 0,
          }}>
            {c.letter}
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: S.textPrimary }}>{c.name}</div>
            <CategoryBadge category={c.category} />
          </div>
        </div>
        <StatusBadge status={c.status} />
      </div>
      <div style={{ fontSize: 12.5, color: S.textMuted, lineHeight: 1.5, flex: 1 }}>
        {c.description}
      </div>
      {!isSoon && (
        <button
          onClick={onConfigure}
          style={{
            alignSelf: 'flex-start', padding: '7px 16px',
            background: c.status === 'connected' ? S.bgCard : `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2 || '#722f37'})`,
            color: c.status === 'connected' ? S.textSecondary : '#fff',
            border: c.status === 'connected' ? `1px solid ${S.borderLight}` : 'none',
            borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}
        >
          {c.status === 'connected' ? t('screens.integrations.manage') : t('screens.integrations.configure')}
        </button>
      )}
      {isSoon && (
        <div style={{ fontSize: 11, color: S.textMuted, fontStyle: 'italic' }}>{t('screens.integrations.availableInUpcoming')}</div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, icon, color }) => (
  <div style={{
    background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 16,
    padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 6,
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: (color || S.burgundy) + '14', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon name={icon} color={color || S.burgundy} size={16} />
      </div>
    </div>
    <div style={{ fontSize: 26, fontWeight: 800, color: S.textPrimary, lineHeight: 1 }}>{value}</div>
  </div>
);

export default IntegrationsScreen;
